import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAxiosSwagger } from '../../hooks/useAxiosSwagger';
import { closeIssues, getIssues, Issue } from '../../api/issues';
import { follow, getFollows, getTimeline, previewRequeue, requeueIssues, RequeuePreview, Timeline } from '../data/cockpit';
import { envKind, useCockpit } from '../data/CockpitContext';
import { ago, when } from '../data/format';
import { apiError, usePoll } from '../data/usePoll';
import { useInEnvFrame } from './EnvLayout';
import { CheckRow, Empty, EnvChip, Kv, Loading, OwnerChip, Spinner } from '../components/ui';
import { DOC_COLORS, FlowItem, FlowTimeline } from '../components/FlowTimeline';
import { v2 } from '../paths';
import { attemptsLabel, exactStateLabel, isOffer, NEVER_ARRIVED, issueCacheKey, issueLabel, nextStep, objectName, ownerOfIssue, verdictLook } from './issueModel';
import { IssuesPage } from './IssuesPage';
import { timelineFlow } from './timelineFlow';
import { copyText } from '../data/clipboard';
import { FlowOrderToggle, useFlowOrder } from '../data/flowOrder';
import { ChooseAccountDialog, LinkBookingDialog } from '../components/ExactCompareDialogs';

/** Without /Cockpit/timeline: what the issue itself knows, as a short flow. */
const fallbackFlow = (i: Issue) => {
    const doc = { id: 'doc', label: `${objectName(i.objectType)} ${issueLabel(i)}`, color: DOC_COLORS[0] };
    const items: FlowItem[] = [
        { phase: doc.label },
        { at: i.firstFailedAt, documentId: 'doc', lane: 'cb', look: 'ok', title: 'In Chargebee', detail: i.eventType ?? '' },
        // An object that never arrived was never received: no green card for it.
        ...(i.code === NEVER_ARRIVED ? [] : [{ at: i.firstFailedAt, documentId: 'doc', lane: 'cn', look: 'ok', title: 'Ontvangen', detail: '', app: 'cbrcv' } as FlowItem]),
        {
            at: i.lastFailedAt,
            documentId: 'doc',
            lane: i.exactState === 'InExact' ? 'ex' : 'cn',
            look: 'bad',
            title: i.title,
            detail: attemptsLabel(i) ?? '',
            app: i.exactState === 'InExact' ? 'exsnd' : undefined,
        },
        { now: when(new Date()) },
    ];
    return { items, documents: [doc] };
};

export const IssuePanel = () => {
    const { customer, environment, key } = useParams();
    const axios = useAxiosSwagger();
    const navigate = useNavigate();
    const { find } = useCockpit();
    const env = find(customer, environment);
    const [confirm, setConfirm] = useState(false);
    // Opened with ?aanbieden=1 from the object page's "Opnieuw aanbieden": the dialog opens once the checks allow it.
    const [params, setParams] = useSearchParams();
    const [closing, setClosing] = useState(false);
    const [reason, setReason] = useState('');
    const [result, setResult] = useState<{ text: string; tone: 'ok' | 'bad' | 'info' }>();
    const [followed, setFollowed] = useState(false);
    const [copied, setCopied] = useState(false);
    // The dialogs that compare with Exact: choosing another account, or linking a booking Exact already has.
    const [comparing, setComparing] = useState<'relink' | 'link' | null>(null);

    const relation = (key ?? '').split('|').pop() ?? '';
    const issue = usePoll<Issue | undefined>(
        axios && customer && environment && key
            ? async (s) => (await getIssues(axios, { customer, environment, q: relation, status: 'all' }, s)).items.find((i) => i.key === key)
            : null,
        15000,
        [axios, customer, environment, key],
        issueCacheKey(customer ?? '', environment ?? '', key ?? '')
    );
    const timeline = usePoll<Timeline>(
        axios && customer && environment && key ? (s) => getTimeline(axios, customer, environment, { key }, s) : null,
        15000,
        [axios, customer, environment, key],
        `timeline/${customer}/${environment}/issue:${key}`
    );
    const preview = usePoll<RequeuePreview | undefined>(
        axios && customer && environment && key ? async (s) => (await previewRequeue(axios, [{ customer, environment, key }], s))[0] : null,
        60000,
        [axios, customer, environment, key],
        `preview/${customer}/${environment}/${key}`
    );
    const inFrame = useInEnvFrame();
    // Asking the server for the latest, also when what is shown came from the list or an earlier visit.
    const refreshing = issue.loading || timeline.loading || preview.loading;
    const follows = usePoll(axios ? (s: AbortSignal) => getFollows(axios, s) : null, 30000, [axios]);

    const i = issue.data;
    const [order, setOrder] = useFlowOrder();
    // Nothing in place of the timeline until it answered: the short flow of the issue first and then the real one jumped.
    const flow = useMemo(
        () => (timeline.data && timeline.data.events.length > 0 ? timelineFlow(timeline.data, undefined, { order }) : !timeline.pending && i ? fallbackFlow(i) : undefined),
        [timeline.data, timeline.pending, i, order]
    );

    // Back to the list as it was: its tile and group came along in the query string.
    const close = () => {
        if (!customer || !environment) return;
        const back = new URLSearchParams(params);
        back.delete('aanbieden');
        back.delete('actie');
        const query = back.toString();
        navigate(`${inFrame ? v2.envIssues(customer, environment) : v2.issues()}${query ? `?${query}` : ''}`);
    };

    // Esc closes what is on top: the dialog first, then the reason field, then the panel.
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key !== 'Escape') return;
            if (comparing) setComparing(null);
            else if (confirm) setConfirm(false);
            else if (closing) setClosing(false);
            else close();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [confirm, closing, comparing, customer, environment]);

    const offerable = !!i && isOffer(nextStep(i, preview.data).action);
    useEffect(() => {
        if (params.get('aanbieden') !== '1' || !offerable) return;
        setConfirm(true);
        const next = new URLSearchParams(params);
        next.delete('aanbieden');
        setParams(next, { replace: true });
    }, [params, offerable, setParams]);

    // Opened with ?actie=link or ?actie=relink from the object page: that dialog at once, once the issue is in.
    const wanted = params.get('actie');
    const loaded = !!i;
    useEffect(() => {
        if (!loaded || (wanted !== 'link' && wanted !== 'relink')) return;
        setComparing(wanted);
        const next = new URLSearchParams(params);
        next.delete('actie');
        setParams(next, { replace: true });
    }, [params, wanted, loaded, setParams]);

    if (!customer || !environment || !key) return null;

    /**
     * The address of this issue on the clipboard. Copied at once within the click, which every browser allows; the
     * clipboard API only where that fails, because it can wait on a permission and then the button looked dead.
     */
    const copyLink = () => {
        setCopied(copyText(window.location.href));
        setTimeout(() => setCopied(false), 1500);
    };
    const owner = i ? ownerOfIssue(i) : 'us';
    const kind = env ? envKind(env) : environment.toLowerCase().startsWith('prod') ? 'prod' : 'test';
    const isFollowed = followed || (follows.data ?? []).some((f) => f.customer === customer && f.environment === environment && f.relationId === i?.relationId);
    const look = verdictLook(preview.data);
    const canOffer = preview.data?.verdict === 'may';
    const resolved = i && (i.status === 'Resolved' || i.status === 'Closed');
    const step = i ? nextStep(i, preview.data) : undefined;
    const badge = step?.who === 'done' ? 'au' : step?.who ?? owner;

    const reloadAll = () => {
        issue.reload();
        timeline.reload();
        preview.reload();
    };

    const doFollow = async () => {
        if (!axios || !i) return;
        try {
            await follow(axios, { customer, environment, objectType: i.objectType, relationId: i.relationId, label: issueLabel(i) });
            setFollowed(true);
        } catch {
            setResult({ text: 'Volgen lukte niet', tone: 'bad' });
        }
    };

    const doOffer = async () => {
        if (!axios) return;
        setConfirm(false);
        setResult({ text: 'Aanbieden…', tone: 'info' });
        try {
            const [r] = await requeueIssues(axios, [{ customer, environment, key }]);
            setResult(r && r.state !== 'Refused' && r.state !== 'Failed' ? { text: `Aangeboden${r.state === 'DryRun' ? ' (proefdraai)' : ''}: we volgen het live`, tone: 'info' } : { text: `Tegengehouden: ${r?.note ?? 'onbekend'}`, tone: 'bad' });
            if (!isFollowed) doFollow();
            issue.reload();
            timeline.reload();
        } catch (e: any) {
            setResult({ text: apiError(e), tone: 'bad' });
        }
    };

    const doClose = async () => {
        if (!axios || !reason.trim()) return;
        try {
            await closeIssues(axios, [{ customer, environment, key }], reason.trim());
            setClosing(false);
            setResult({ text: 'Gesloten', tone: 'ok' });
            issue.reload();
        } catch {
            setResult({ text: 'Sluiten lukte niet', tone: 'bad' });
        }
    };

    return (
        <>
            <IssuesPage />
            <button className="scrim" aria-label="Paneel sluiten" onClick={close} />
            <aside className="drawer" aria-label="Issue" aria-busy={refreshing}>
                <div className={`dprog ${refreshing ? 'on' : ''}`} aria-hidden="true" />
                <div className="dhead">
                    <div className="hrow">
                        {!inFrame && (
                            <>
                                <EnvChip kind={kind} environment={environment} />
                                <span className="mut">{customer}</span>
                                <span className="mut">/</span>
                            </>
                        )}
                        <span className="mono">{i ? issueLabel(i) : relation}</span>
                        <span className="mut hrow" style={{ fontSize: 12, gap: 6 }}>
                            {refreshing ? (
                                <>
                                    <Spinner />
                                    {i ? 'laatste gegevens ophalen…' : 'ophalen…'}
                                </>
                            ) : issue.loadedAt ? (
                                `bijgewerkt ${ago(issue.loadedAt)} geleden`
                            ) : null}
                        </span>
                        <span className="grow" />
                        {i && (
                            <Link
                                className="btn sm ghost"
                                to={v2.object(i.customer, i.environment, i.invoiceId || i.relationId)}
                                state={{ from: window.location.pathname + window.location.search, label: `issue ${issueLabel(i)}` }}
                            >
                                Dossier
                            </Link>
                        )}
                        <button className={isFollowed ? 'btn sm' : 'btn sm ghost'} onClick={doFollow} disabled={isFollowed}>
                            {isFollowed ? 'Gevolgd' : 'Volgen'}
                        </button>
                        <button className="btn sm ghost" onClick={copyLink} title="Kopieer de link naar dit issue">
                            {copied ? 'Gekopieerd' : 'Link'}
                        </button>
                        <button className="btn sm ghost" onClick={close} aria-label="Sluiten" title="Sluiten (Esc)">
                            Esc
                        </button>
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 600, lineHeight: 1.25 }}>{i ? i.interpretation?.title || i.title : issue.pending || issue.loading ? ' ' : 'Issue niet gevonden'}</div>
                    {i && (
                        <div className="hrow">
                            <OwnerChip owner={owner} long />
                            {i.unrecognised && <span className="chip warn">Nog geen regel</span>}
                            <span className="chip">Open {ago(i.firstFailedAt)}</span>
                            {attemptsLabel(i) && <span className="chip">{attemptsLabel(i)}</span>}
                            {i.reviewNote && <span className="chip info" title={i.reviewNote}>Herbeoordeeld</span>}
                        </div>
                    )}
                </div>

                {issue.error && <div className="dsec"><div className="notice">{issue.error}</div></div>}
                {!i && issue.pending && (
                    <div className="dsec" style={{ minHeight: 160 }}>
                        <Loading>Issue ophalen…</Loading>
                    </div>
                )}

                {i && step && (
                    <div className="dsec">
                        <div className={`next ${badge}`}>
                            <span className={`who ${badge}`}>{step.who === 'done' ? '✓' : step.who === 'us' ? 'WIJ' : step.who === 'cu' ? 'KLANT' : '↻'}</span>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                <div style={{ fontSize: 18, fontWeight: 600 }}>{step.title}</div>
                                {step.text && <div>{step.text}</div>}
                                {((i.interpretation?.explanation && i.interpretation.explanation !== step.text) ||
                                    (!i.checkable && !i.checksAt && (i.interpretation?.steps?.length ?? 0) > 0)) && (
                                    <details>
                                        <summary className="mut" style={{ cursor: 'pointer', fontSize: 13 }}>Achtergrond van deze fout</summary>
                                        {i.interpretation?.explanation && i.interpretation.explanation !== step.text && (
                                            <div className="mut" style={{ marginTop: 8 }}>{i.interpretation.explanation}</div>
                                        )}
                                        {/* The rule's general steps only where no checker judges this case: they name buttons for other cases. */}
                                        {!i.checkable && !i.checksAt && (i.interpretation?.steps?.length ?? 0) > 0 && (
                                            <ol className="steps-num" style={{ marginTop: 8 }}>
                                                {i.interpretation!.steps!.slice(0, 4).map((s, n) => (
                                                    <li key={n}><span>{s}</span></li>
                                                ))}
                                            </ol>
                                        )}
                                    </details>
                                )}
                                {!resolved && (
                                    <div className="hrow">
                                        {step.action && (
                                            <button
                                                className="btn pri"
                                                onClick={() => (step.action === 'relink' || step.action === 'link' ? setComparing(step.action) : setConfirm(true))}
                                            >
                                                {step.actionLabel}
                                            </button>
                                        )}
                                        {owner === 'cu' && (
                                            <Link className="btn" to={`${v2.learn('mails')}?klant=${encodeURIComponent(customer)}&omgeving=${encodeURIComponent(environment)}`}>
                                                Klantmail
                                            </Link>
                                        )}
                                        <button className="btn ghost" onClick={() => setClosing(!closing)}>Sluiten met reden</button>
                                    </div>
                                )}
                                {closing && (
                                    <div className="hrow">
                                        <label className="search" style={{ flex: 1 }}>
                                            <input aria-label="Reden" placeholder="Waarom sluiten?" value={reason} onChange={(e) => setReason(e.target.value)} />
                                        </label>
                                        <button className="btn" disabled={!reason.trim()} onClick={doClose}>Sluiten</button>
                                    </div>
                                )}
                                {result && (
                                    <div className={`hrow ${result.tone}`} style={{ gap: 6 }}>
                                        {result.tone === 'info' && <Spinner />}
                                        {result.text}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                <div className="dsec">
                    <div className="hrow" style={{ marginBottom: 10, justifyContent: 'space-between' }}>
                        <span className="ct">Timeline</span>
                        <span className="hrow" style={{ gap: 8 }}>
                            {timeline.error && <span className="mut" style={{ fontSize: 12 }}>uit het issue zelf</span>}
                            <FlowOrderToggle order={order} onChange={setOrder} />
                        </span>
                    </div>
                    <div style={{ minHeight: flow ? undefined : 220 }}>
                        {flow ? <FlowTimeline items={flow.items} documents={flow.documents} /> : timeline.pending ? <Loading>Timeline ophalen…</Loading> : <Empty>Geen stappen gevonden.</Empty>}
                    </div>
                </div>

                {i && (
                    <div className="dsec g2e">
                        <div>
                            <div className="ct" style={{ marginBottom: 6 }}>Zelf nagekeken</div>
                            <div className="checks">
                                {i.checks.length === 0 && <span className="mut" style={{ fontSize: 13 }}>{i.checkable ? 'Nog niet nagekeken' : 'Geen controle voor deze fout'}</span>}
                                {i.checks.map((c, n) => (
                                    <CheckRow key={n} tone={c.ok ? 'ok' : 'bad'} label={c.text} />
                                ))}
                                <CheckRow
                                    tone={canOffer ? 'ok' : preview.data?.verdict === 'no' ? 'bad' : 'wait'}
                                    label={
                                        <>
                                            Opnieuw aanbieden
                                            {look.reason && <div className="mut" style={{ fontSize: 12, marginTop: 2 }}>{look.reason}</div>}
                                        </>
                                    }
                                    value={look.text}
                                />
                            </div>
                            {i.checksAt && <div className="mut" style={{ fontSize: 12, marginTop: 6 }}>nagekeken {ago(i.checksAt)} geleden{i.checkedInExact ? ' · in Exact' : ''}</div>}
                        </div>
                        <div>
                            <div className="ct" style={{ marginBottom: 6 }}>Feiten</div>
                            <Kv
                                rows={[
                                    ['Soort', objectName(i.objectType)],
                                    ['Id', <span className="mono">{i.relationId}</span>],
                                    ['Factuur', i.invoiceId ? <span className="mono">{i.invoiceId}</span> : '–'],
                                    ['Klant', i.chargebeeCustomerId ? <span className="mono">{i.chargebeeCustomerId}</span> : '–'],
                                    ['In Exact', exactStateLabel(i.exactState)],
                                    ['Eerste fout', when(i.firstFailedAt)],
                                    ['Laatste fout', when(i.lastFailedAt)],
                                    ['Probleemcode', <span className="mono">{i.code}</span>],
                                ]}
                            />
                        </div>
                    </div>
                )}

                {i?.errorMessage && (
                    <div className="dsec">
                        <details>
                            <summary className="ct" style={{ cursor: 'pointer' }}>Melding van de connector</summary>
                            <div className="codebox" style={{ marginTop: 8 }}>{i.errorMessage}</div>
                        </details>
                    </div>
                )}

                {i && (
                    <div className="dsec">
                        <div className="ct" style={{ marginBottom: 6 }}>Activiteit</div>
                        <div className="checks">
                            {i.closedAt && <div className="chk" style={{ gridTemplateColumns: '110px 100px minmax(0,1fr)' }}><span className="mono mut">{when(i.closedAt)}</span><span className="own au">{i.closedBy ?? 'iemand'}</span><span>Gesloten: {i.closedReason}</span></div>}
                            {i.reviewedAt && <div className="chk" style={{ gridTemplateColumns: '110px 100px minmax(0,1fr)' }}><span className="mono mut">{when(i.reviewedAt)}</span><span className="own au">{i.reviewedBy ?? 'Herbeoordeling'}</span><span>{i.reviewNote}</span></div>}
                            {i.autoRequeueNote && <div className="chk" style={{ gridTemplateColumns: '110px 100px minmax(0,1fr)' }}><span className="mono mut">{when(i.nextAutoRequeueAt ?? i.lastCheckedAt)}</span><span className="own au">{i.autoRequeueBy ?? 'Scanner'}</span><span>{i.autoRequeueNote}</span></div>}
                            {i.lastCheckedAt && <div className="chk" style={{ gridTemplateColumns: '110px 100px minmax(0,1fr)' }}><span className="mono mut">{when(i.lastCheckedAt)}</span><span className="own au">Scanner</span><span>Nagekeken</span></div>}
                            <div className="chk" style={{ gridTemplateColumns: '110px 100px minmax(0,1fr)' }}><span className="mono mut">{when(i.firstFailedAt)}</span><span className="own au">Connector</span><span>Eerste mislukte poging</span></div>
                        </div>
                    </div>
                )}
            </aside>

            {axios && comparing === 'relink' && i && <ChooseAccountDialog axios={axios} issue={i} kind={kind} onClose={() => setComparing(null)} onDone={reloadAll} />}
            {axios && comparing === 'link' && i && <LinkBookingDialog axios={axios} issue={i} kind={kind} onClose={() => setComparing(null)} onDone={reloadAll} />}

            {confirm && i && (
                <>
                    <button className="scrim dialog-scrim" aria-label="Annuleren" onClick={() => setConfirm(false)} />
                    <div className="dialog" role="dialog" aria-label="Opnieuw aanbieden">
                        <div className="ch">
                            <span className="h2">{objectName(i.objectType)} {issueLabel(i)} {step?.action === 'fetch' ? 'ophalen uit Chargebee' : 'opnieuw aanbieden'}</span>
                            <EnvChip kind={kind} environment={`${customer} ${environment}`} />
                        </div>
                        <div className="cb g2e">
                            <div>
                                <div className="eyebrow" style={{ marginBottom: 6 }}>Gaat opnieuw</div>
                                <div className="checks">
                                    {step?.action === 'fetch' && <CheckRow tone="wait" mark="0" label="Ophalen uit Chargebee, zoals het nu is" value="Chargebee" />}
                                    <CheckRow tone="wait" mark="1" label={`${objectName(i.objectType)} naar de connector`} value="queue" />
                                    <CheckRow tone="wait" mark="2" label="Daarna naar Exact" value="exsnd" />
                                </div>
                            </div>
                            <div>
                                <div className="eyebrow" style={{ marginBottom: 6 }}>Vooraf gecontroleerd</div>
                                <div className="checks">
                                    <CheckRow tone="ok" label="Controles" value={look.text} />
                                    {i.checks.slice(0, 3).map((c, n) => (
                                        <CheckRow key={n} tone={c.ok ? 'ok' : 'bad'} label={c.text} />
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="ch" style={{ borderTop: '1px solid #1A212B', borderBottom: 0, justifyContent: 'flex-end' }}>
                            <button className="btn ghost" onClick={() => setConfirm(false)}>Annuleren</button>
                            <button className="btn pri" onClick={doOffer}>Aanbieden en volgen</button>
                        </div>
                    </div>
                </>
            )}
        </>
    );
};
